import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import QRDownloader from 'components/Utility/QRDownloader';
import URLPreview from 'components/Utility/URLPreview';
import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Loader from 'react-overlay-loader/lib/Loader';
import styled, { css } from 'styled-components';
import { ServiceTypes } from 'utils/constants/clients';
import Checkbox from '../../MenuEditor/components/shared/Checkbox';
import DeleteButton from '../../MenuEditor/components/shared/DeleteButton';
import MenuTag from '../../MenuEditor/components/shared/MenuTag';
import './index.scss';
import ServiceTypeSelect from './ServiceTypeSelect';

const ClientDetailsHeader = styled.div`
  font-family: 'Basier Circle';
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.20000000298023224px;
  text-align: left;
  margin-bottom: 8px;

  ${props =>
    props.tall
      ? css`
          margin-bottom: 15px;
        `
      : ``}
`;

const ClientDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;

  margin-bottom: 35px;

  ${props =>
    props.slim
      ? css`
          margin-bottom: 10px;
        `
      : ``}
`;

const ClientDetailsText = styled.div`
  font-family: 'Basier Circle';
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.20000000298023224px;
  text-align: left;
`;

const ClientDetailsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 50px;
`;

const ClientDetailsCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const CTAButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-grow: 1;
  width: 100%;
`;

const ClientFormActionRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const ClientDetailsMenuRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
`;

const GetMenuFromMenuId = (menus, menuId) => menus.find(m => m.id === menuId);

const ServiceTypeLabelFromValue = serviceType => {
  switch (serviceType) {
    case ServiceTypes.DROPOFF_AND_COLLECTION:
      return 'Drop off + Collection';
    case ServiceTypes.DROPOFF_ONLY:
      return 'Drop off Only';
    case ServiceTypes.COLLECTION_ONLY:
      return 'Collection Only';
    default:
      return 'Unknown';
  }
};

const ClientDetails = ({ loading, client, venueSlug, menus }) => {
  const url = `${process.env.REACT_APP_CUSTOMER_APP_URL.substr(
    8
  )}/venue/${venueSlug}/${client.slug}`;

  return (
    <React.Fragment>
      <div style={{ marginTop: 25 }} />
      {client.notes && (
        <ClientDetailsContainer>
          <ClientDetailsHeader>Notes</ClientDetailsHeader>
          <ClientDetailsText>{client.notes}</ClientDetailsText>
        </ClientDetailsContainer>
      )}

      <ClientDetailsContainer>
        <ClientDetailsHeader>Menus</ClientDetailsHeader>
        {client.menuIds.map(mid => {
          const menu = GetMenuFromMenuId(menus, mid);
          if (!menu) {
            return <React.Fragment></React.Fragment>;
          }
          return (
            <ClientDetailsMenuRow key={mid}>
              <ClientDetailsText>{menu.name}</ClientDetailsText>
              {menu.clientOnly && <MenuTag>Clients</MenuTag>}
            </ClientDetailsMenuRow>
          );
        })}
      </ClientDetailsContainer>

      <ClientDetailsContainer>
        <ClientDetailsRow>
          <ClientDetailsCol>
            <ClientDetailsHeader>Service Type</ClientDetailsHeader>
            <ClientDetailsText>
              {ServiceTypeLabelFromValue(client.serviceType)}
            </ClientDetailsText>
          </ClientDetailsCol>
          <ClientDetailsCol>
            <ClientDetailsHeader>Accept Pre-orders?</ClientDetailsHeader>
            <ClientDetailsText>
              {client.acceptPreorders ? `Yes` : `No`}
            </ClientDetailsText>
          </ClientDetailsCol>
          <ClientDetailsCol>
            <ClientDetailsHeader>Discount</ClientDetailsHeader>
            <ClientDetailsText>{client.discount}%</ClientDetailsText>
          </ClientDetailsCol>
        </ClientDetailsRow>
      </ClientDetailsContainer>

      <ClientDetailsContainer>
        <ClientDetailsHeader tall>
          Link to this version of your Tabb page
        </ClientDetailsHeader>
        <URLPreview>{url}</URLPreview>
      </ClientDetailsContainer>

      <ClientDetailsContainer slim>
        <ClientDetailsHeader tall>Download your QR Code</ClientDetailsHeader>
        <QRDownloader url={url}></QRDownloader>
      </ClientDetailsContainer>

      <Loader loading={loading} />
    </React.Fragment>
  );
};

const ClientForm = ({
  client,
  venueSlug,
  menus,
  onCancel,
  onDelete,
  onSave,
  editing,
  loading,
  expanded,
}) => {
  const isEditing = editing ? true : client ? false : true;
  const canDelete = isEditing && client;

  const [emptySlug, setEmptySlug] = useState(false);
  const formRef = useRef();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: client
      ? {
          ...client,
        }
      : {
          serviceType: ServiceTypes.DROPOFF_AND_COLLECTION,
          menuIds: [],
          acceptPreorders: true,
          discount: 0,
          name: '',
          notes: '',
          slug: '',
        },
  });

  const numericValueValidator = num => /^[0-9]*$/.test(num);

  const slug = watch('slug');
  const serviceType = watch('serviceType');
  const menuIds = watch('menuIds') || [];
  const acceptPreorders = watch('acceptPreorders');

  if (slug === '' && !emptySlug) {
    setEmptySlug(true);
  }

  const selectMenu = menuId => {
    const index = menuIds.indexOf(menuId);
    const newValues = [...menuIds];

    if (index === -1) {
      newValues.push(menuId);
    } else {
      newValues.splice(index, 1);
    }

    setValue('menuIds', newValues);
  };

  const changeServiceType = e => {
    setValue('serviceType', e.target.value);
  };

  const convertToSlug = value =>
    value
      .replaceAll(' ', '-')
      .replace(/[^A-Za-z0-9\-]/g, '')
      .toLowerCase();

  const autoSlugByName = value => {
    if (emptySlug) {
      setValue('slug', convertToSlug(value));
    }
  };

  const disableForm = loading;

  const onSubmit = data => {
    const newData = { ...data };

    // Discount needs to be converted to a numerical value as the Controller doesn't support
    // valueAsNumber
    newData.discount = parseInt(data.discount, 10);

    if (onSave) {
      onSave(newData);
    }
  };

  const onFormCancel = () => {
    reset();

    if (onCancel) {
      onCancel();
    }
  };

  const getCommonTextfieldProps = (field, valMessage) => ({
    error: !!errors[field],
    helperText:
      errors[field] && errors[field].message
        ? errors[field].message
        : errors[field] && errors[field].validation
        ? errors[field].validation
        : '',
    disabled: disableForm,
  });

  return !isEditing ? (
    <ClientDetails
      loading={loading}
      client={client}
      venueSlug={venueSlug}
      menus={menus}
    />
  ) : (
    <form
      style={{ marginTop: client ? 20 : 0 }}
      ref={formRef}
      className="client__form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        name="name"
        control={control}
        defaultValue=""
        rules={{ required: 'Name is Required' }}
        render={({ field }) => (
          <TextField
            {...getCommonTextfieldProps('name')}
            onInput={e => autoSlugByName(e.target.value)}
            classes={{ root: 'marginb__md client__form__field__small' }}
            InputLabelProps={{
              shrink: true,
              classes: { root: 'input__label' },
            }}
            label="Name"
            placeholder="e.g Client Name"
            {...field}
          />
        )}
      />

      <Controller
        name="slug"
        control={control}
        defaultValue=""
        rules={{ required: 'URL is Required' }}
        render={({ field }) => (
          <TextField
            {...getCommonTextfieldProps('slug')}
            style={{ maxWidth: 550 }}
            onInput={e => (e.target.value = convertToSlug(e.target.value))}
            classes={{ root: 'marginb__md' }}
            InputLabelProps={{
              shrink: true,
              classes: { root: 'input__label' },
            }}
            label="Page URL"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  disableTypography={true}
                  classes={{ root: 'client__form__field__adornment' }}
                  position="start"
                >{`${process.env.REACT_APP_CUSTOMER_APP_URL.substr(
                  8
                )}/venue/${venueSlug}/`}</InputAdornment>
              ),
            }}
            {...field}
          />
        )}
      />

      <Controller
        name="notes"
        control={control}
        defaultValue=""
        rules={{ required: false }}
        render={({ field }) => (
          <TextField
            disabled={disableForm}
            classes={{ root: 'marginb__md client__form__field__small' }}
            InputLabelProps={{
              shrink: true,
              classes: { root: 'input__label' },
            }}
            label="Notes"
            placeholder="e.g Address/Location"
            {...field}
          />
        )}
      />

      <FormControl error={!!errors.menuIds}>
        <FormLabel classes={{ root: 'input__label marginb__sm' }}>
          Include these menus
        </FormLabel>

        {menus.map(menuData => (
          <Checkbox
            disabled={disableForm}
            key={menuData.id}
            onSelect={() => selectMenu(menuData.id)}
            isChecked={menuIds ? menuIds.indexOf(menuData.id) !== -1 : false}
            label={menuData.name}
          ></Checkbox>
        ))}

        {!!errors.menuIds && (
          <FormHelperText>{errors.menuIds.message}</FormHelperText>
        )}
      </FormControl>

      <div className="marginb__md" />

      <FormLabel classes={{ root: 'input__label marginb__sm' }}>
        Service Type
      </FormLabel>

      <ServiceTypeSelect
        disabled={disableForm}
        value={serviceType}
        onChange={e => changeServiceType(e)}
      />

      <div className="marginb__md" />

      <FormLabel classes={{ root: 'input__label marginb__sm' }}>
        Pre-orders
      </FormLabel>

      <Checkbox
        disabled={disableForm}
        onSelect={() => setValue('acceptPreorders', !acceptPreorders)}
        isChecked={acceptPreorders}
        label="Accept Pre-orders"
      ></Checkbox>

      <div className="marginb__md" />

      <Controller
        name="discount"
        control={control}
        defaultValue=""
        rules={{
          required: true,
          min: { value: 0, message: 'Value must be between 0-99%' },
          max: { value: 99, message: 'Value must be between 0-99%' },
          validate: numericValueValidator,
        }}
        render={({ field }) => (
          <TextField
            {...getCommonTextfieldProps('discount', 'Must be a whole number')}
            helperText={
              errors['discount'] && errors['discount'].type === 'validate'
                ? 'Must be a whole number'
                : ''
            }
            disabled={disableForm}
            classes={{ root: 'marginb__md client__form__field__tiny' }}
            InputLabelProps={{
              shrink: true,
              classes: { root: 'input__label discount__label' },
            }}
            label="Discount (optional)"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  disableTypography={true}
                  classes={{ root: 'client__form__field__adornment' }}
                  position="end"
                >
                  %
                </InputAdornment>
              ),
            }}
            {...field}
          />
        )}
      />

      <ClientFormActionRow>
        <CTAButtonsContainer>
          <Button
            onClick={() => {
              if (menuIds.length === 0) {
                setError('menuIds', {
                  type: 'required',
                  message: 'You must select at least one Menu',
                });
              } else {
                clearErrors('menuIds');
              }

              if (formRef.current) {
                formRef.current.dispatchEvent(
                  new Event('submit', { cancelable: true })
                );
              }
            }}
            className="fatsoma__cta"
          >
            Save
          </Button>
          <Button
            onClick={onFormCancel}
            className="fatsoma__cta fatsoma__cta__secondary"
          >
            Cancel
          </Button>
        </CTAButtonsContainer>

        {canDelete && (
          <DeleteButton
            onClick={() => {
              if (onDelete) {
                onDelete();
              }
            }}
            variant="secondary"
          >
            Delete Client
          </DeleteButton>
        )}
      </ClientFormActionRow>

      <Loader loading={loading} />
    </form>
  );
};

export default ClientForm;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/Client/ClientForm/index.jsx